import * as React from "react";
import { motion, AnimatePresence } from "motion/react";
import { Sparkles, RefreshCw, AlertTriangle, AlertCircle, Loader2 } from "lucide-react";
import { Button, Card, CardContent, cn } from "@eduviaos/ui-kit";
import { ApiError } from "@eduviaos/shared";
import { useAiSuggestion } from "@/hooks/useAdmissions";

/**
 * AISuggestionChip — Phase E2b
 * --------------------------------------------------------------
 * The first user-facing AI surface in EduViaOS. Sits on the IA's
 * enquiry detail page right rail. The IA clicks "Get AI suggestion"
 * and the BE runs the E2a provider chain (Gemini primary → NVIDIA
 * NIM fallback) to produce a one-line counsellor recommendation.
 *
 * Why opt-in (not auto-fetch on page load)?
 *   * AI calls cost real money per impression. We don't fire one
 *     unless the IA actually wants the recommendation.
 *   * The chip preserves the user's agency — "the AI nudged me"
 *     reads very differently from "the AI told me what to do".
 *
 * Three meaningful states, each visually distinct:
 *   1. Idle / not-yet-asked → "Get AI suggestion" CTA + sparkle icon.
 *   2. Loading → spinner + animated shimmer copy ("Asking <model>…").
 *   3. Success → suggestion body, provider attribution, fallback
 *      pill (when fallback_depth > 0), regenerate button.
 *   4. Error 503 (AI_UNAVAILABLE) → calm warning + Retry CTA.
 *   5. Error other → red alert + Retry CTA.
 *
 * Motion budget (Iron Rule #6 + emil-design-eng skill):
 *   * Body fade-in 180ms, easeOut, opacity + 4px translateY only.
 *   * Provider chip 120ms slide.
 *   * Loading shimmer is opacity-based (no translateX) so we
 *     respect prefers-reduced-motion via the `m.div`.
 *   * No layout-shift animations on width/height — would jank the
 *     surrounding right-rail cards.
 *
 * A11y:
 *   * The card has `aria-live="polite"` so a screen reader announces
 *     the suggestion when it lands.
 *   * The retry button has an explicit `aria-label` describing
 *     what's being retried (the model is in the chip).
 *   * Color is never the sole carrier of meaning — the resilience
 *     state has both an amber pill AND the words "Resilience mode".
 */
export function AISuggestionChip({
  enquiryId,
  className,
}: {
  enquiryId: string;
  className?: string;
}) {
  // Opt-in: only flip `enabled` once the IA clicks the CTA.
  const [enabled, setEnabled] = React.useState(false);
  const { data, isLoading, isError, error, refetch, isRefetching } = useAiSuggestion(
    enquiryId,
    enabled,
  );

  const onAsk = React.useCallback(() => {
    setEnabled(true);
  }, []);

  const onRetry = React.useCallback(() => {
    if (!enabled) {
      setEnabled(true);
      return;
    }
    void refetch();
  }, [enabled, refetch]);

  const showLoading = enabled && (isLoading || isRefetching);
  const showError = enabled && isError && !showLoading;
  const showSuccess = enabled && data && !showLoading && !showError;

  return (
    <Card
      className={cn(
        "border-border/60 overflow-hidden",
        // Subtle iridescent border when a suggestion is live, so the
        // chip reads as a distinct surface from the standard cards
        // around it without shouting.
        showSuccess &&
          "border-violet-300/60 bg-gradient-to-br from-violet-50/50 to-transparent dark:border-violet-500/30 dark:from-violet-950/20",
        className,
      )}
      data-testid="ai-suggestion-chip"
      aria-live="polite"
    >
      <CardContent className="space-y-3 p-4">
        {/* Header row — present in every state for layout stability */}
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "inline-flex size-7 items-center justify-center rounded-md",
              showSuccess
                ? "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300"
                : "bg-muted text-muted-foreground",
            )}
            aria-hidden="true"
          >
            <Sparkles className="size-3.5" />
          </span>
          <div className="flex-1 leading-tight">
            <p className="text-foreground text-xs font-semibold">AI counsellor suggestion</p>
            <p className="text-muted-foreground text-[10px] uppercase tracking-wider">
              Powered by EduViaOS AI
            </p>
          </div>
        </div>

        <AnimatePresence mode="wait" initial={false}>
          {!enabled && (
            <motion.div
              key="idle"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              className="space-y-2"
            >
              <p className="text-muted-foreground text-xs leading-relaxed">
                Get a one-line, AI-suggested next action for this enquiry. Uses your enquiry context
                (stage, parent, notes) — nothing else.
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={onAsk}
                className="w-full"
                data-testid="ai-suggestion-ask"
              >
                <Sparkles className="size-3.5" aria-hidden="true" />
                Get AI suggestion
              </Button>
            </motion.div>
          )}

          {showLoading && (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              className="space-y-2"
              data-testid="ai-suggestion-loading"
            >
              <div className="flex items-center gap-2 text-xs">
                <Loader2 className="size-3.5 animate-spin text-violet-600" aria-hidden="true" />
                <ShimmerText>Thinking…</ShimmerText>
              </div>
              <div className="space-y-1.5" aria-hidden="true">
                <div className="bg-muted h-3 w-full animate-pulse rounded" />
                <div className="bg-muted h-3 w-4/5 animate-pulse rounded" />
              </div>
            </motion.div>
          )}

          {showSuccess && data && (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="space-y-3"
              data-testid="ai-suggestion-success"
            >
              <p
                className="text-foreground text-sm leading-relaxed"
                data-testid="ai-suggestion-text"
              >
                {data.suggestion}
              </p>

              <div className="flex flex-wrap items-center gap-1.5">
                {data.fallback_depth > 0 && <ResilienceModePill />}
                <StalenessPill generatedAt={data.generated_at} />
              </div>

              <div className="border-border/60 flex items-center justify-between gap-2 border-t pt-2.5">
                <span className="text-muted-foreground text-[10px] tabular-nums">
                  {data.latency_ms}ms
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onRetry}
                  disabled={isRefetching}
                  className="h-7 px-2 text-xs"
                  aria-label="Regenerate AI suggestion"
                  data-testid="ai-suggestion-regenerate"
                >
                  <RefreshCw
                    className={cn("size-3", isRefetching && "animate-spin")}
                    aria-hidden="true"
                  />
                  Regenerate
                </Button>
              </div>
            </motion.div>
          )}

          {showError && (
            <motion.div
              key="error"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              className="space-y-2"
              data-testid="ai-suggestion-error"
            >
              <ErrorBlock error={error ?? null} onRetry={onRetry} />
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────

function ResilienceModePill() {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium",
        "border-amber-300/60 bg-amber-50 text-amber-900",
        "dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-200",
      )}
      title="AI is running in resilience mode — answers may be slightly slower."
      data-testid="ai-suggestion-fallback-pill"
    >
      <AlertTriangle className="size-2.5" aria-hidden="true" />
      Resilience mode
    </span>
  );
}

function StalenessPill({ generatedAt }: { generatedAt: string }) {
  const [, setTick] = React.useState(0);
  // Re-render the staleness label every 30 s so "5m ago" doesn't lie
  // when the IA leaves the page open. Using a tick state is cheaper
  // than wiring useQuery to a polling interval.
  React.useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const label = formatRelative(generatedAt);
  return (
    <span
      className="text-muted-foreground border-border/60 bg-muted/40 inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-medium"
      title={`Generated at ${new Date(generatedAt).toLocaleString()}`}
    >
      {label}
    </span>
  );
}

function ErrorBlock({ error, onRetry }: { error: ApiError | null; onRetry: () => void }) {
  const isUnavailable = error instanceof ApiError && error.code === "AI_UNAVAILABLE";

  return (
    <div
      role="alert"
      className={cn(
        "flex items-start gap-2 rounded-md border px-3 py-2 text-xs",
        isUnavailable
          ? "border-amber-300/60 bg-amber-50 text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-200"
          : "border-destructive/30 bg-destructive/5 text-destructive",
      )}
    >
      {isUnavailable ? (
        <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
      ) : (
        <AlertCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
      )}
      <div className="min-w-0 flex-1">
        <p className="font-medium">
          {isUnavailable ? "AI temporarily unavailable" : "Couldn't reach AI"}
        </p>
        <p
          className={cn(
            "mt-0.5 text-[11px] leading-relaxed",
            !isUnavailable && "text-destructive/80",
          )}
        >
          {isUnavailable
            ? "AI is temporarily unavailable. Try again in a minute, or proceed without the AI nudge."
            : (error?.message ?? "Unknown error.")}
        </p>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRetry}
          className="mt-1.5 h-6 px-2 text-[11px]"
          data-testid="ai-suggestion-retry"
        >
          <RefreshCw className="size-3" aria-hidden="true" />
          Try again
        </Button>
      </div>
    </div>
  );
}

function ShimmerText({ children }: { children: React.ReactNode }) {
  // Opacity-only shimmer (no translateX) so respects
  // prefers-reduced-motion gracefully via Motion's `m` defaults.
  return (
    <motion.span
      animate={{ opacity: [0.55, 1, 0.55] }}
      transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
      className="text-muted-foreground"
    >
      {children}
    </motion.span>
  );
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "just now";
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (seconds < 5) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}
